import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import { compact } from "lodash";
import { useEffect } from "react";

import { classNames } from "@helpers/css";

const TextOnlyWysiwyg = ({
  deps = [],
  value,
  editable = false,
  autofocus = false,
  placeholder = "",
  className = "",
  onChangeValue,
  onEnterPressed,
  onEditorReady,
}: {
  deps?: any[];
  value?: string | null;
  autofocus?: boolean;
  editable?: boolean;
  placeholder?: string;
  className?: string;
  onChangeValue?: (value: string) => void;
  onEnterPressed?: () => void;
  onEditorReady?: (editor: Editor) => void;
}) => {
  const content = !value // if falsy, return null
    ? null
    : value; // or we just return the string value.
  const editor = useEditor(
    {
      editable,
      autofocus,
      extensions: compact([
        Text,
        Paragraph,
        HardBreak.configure({ keepMarks: false }),
        Document,
        Placeholder.configure({
          placeholder: placeholder,
          showOnlyCurrent: true,
        }),
      ]),
      editorProps: {
        attributes: {
          class: classNames(className, "no-placeholder-on-every-line"),
        },
        handleKeyDown(view, event) {
          if (event.key === "Enter") {
            if (event.shiftKey) {
              view.dispatch(
                view.state.tr
                  .replaceSelectionWith(
                    view.state.schema.nodes.hardBreak.create()
                  )
                  .scrollIntoView()
              );
              return true;
            } else {
              event.preventDefault();
              onEnterPressed?.();
              return true;
            }
          }
          return false;
        },
      },
      content,
    },
    deps
  );

  useEffect(() => {
    const handleUpdateValue = ({ editor }: any) => {
      if (onChangeValue) onChangeValue(editor.getText());
    };
    editor?.on("update", handleUpdateValue);
    return function cleanup() {
      editor?.off("update", handleUpdateValue);
    };
  }, [editor, onChangeValue]);

  useEffect(() => {
    if (onEditorReady && editor) onEditorReady(editor);
  }, [editor, onEditorReady]);

  return (
    <>
      <EditorContent editor={editor} className="flex-1 min-w-0" />
    </>
  );
};

export default TextOnlyWysiwyg;
